import { Loading } from '@dbi-tools/ui-kit';
import { Banner, BannerKeys } from '@dbi-tools/ui-kit/lib/components/banner';
import { NotificationList } from '@dbi-tools/ui-kit/lib/notification';
import { ToolsLoader } from '@dbi-tools/ui-kit/lib/tools';
import Head from 'next/head';
import React, { Fragment, ReactNode, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { connect } from 'react-redux';

import {
  ADOBE_LAUNCH,
  APPSTORE_ID,
  BASE_PATH,
  GOOGLE_GTM_ID,
  HOTJAR_ID,
  RECAPTCHA_SITE_KEY,
  TEALIUM_ENV,
} from '../service/variables';
import { NotificationMessage } from '../store/notification/types';
import { RootState } from '../store/root-reducer';
import { APP_NAME } from '../types/app';
import { RecaptchaContext } from './common/recaptcha';
import Header from './header';
import { BodyStyled, MainContentStyled } from './layout.styled';

export interface LayoutProps {
  mainClass?: string;
  rightHeaderSlot?: ReactNode;
  leftHeaderSlot?: ReactNode;
  isLoading: boolean;
  contentStyle?: boolean;
  notFullStyle?: boolean;
  withFooterStyle?: boolean;
  fullHeightStyle?: boolean;
  notifications: NotificationMessage[];
  addRecaptcha?: boolean;
  appName?: APP_NAME;
}

const Layout: React.FC<LayoutProps & { children?: ReactNode }> = ({
  children,
  leftHeaderSlot,
  rightHeaderSlot,
  isLoading,
  mainClass,
  contentStyle = true,
  notFullStyle = true,
  withFooterStyle = false,
  fullHeightStyle = false,
  notifications,
  addRecaptcha = true,
  appName,
}) => {
  const ref = useRef(null);
  const [[resolve, reject], setCallbacks] = useState([null, null]);
  const executeAsync = () => {
    return new Promise<string>((rv, rj) => {
      setCallbacks([rv, rj]);
      ref.current.execute();
    });
  };

  const mainContent = { contentStyle, notFullStyle, withFooterStyle, fullHeightStyle };
  const isVisionBoard = appName === APP_NAME.VisionBoard;

  const Loader = () =>
    isVisionBoard ? <ToolsLoader /> : <Loading color="gray800" full text="Loading..." fontSize={32} type="db" />;

  const content = (
    <div style={{ minHeight: '100vh' }}>
      <Banner active={isVisionBoard ? BannerKeys.PEARL : BannerKeys.DAVIDS} />
      <Header rightSlot={rightHeaderSlot} leftSlot={leftHeaderSlot} {...{ appName }} />
      {isLoading ? (
        <Loader />
      ) : (
        <MainContentStyled {...mainContent} className={mainClass}>
          {children}
        </MainContentStyled>
      )}
      <NotificationList items={notifications} />
    </div>
  );

  return (
    <Fragment>
      <BodyStyled />
      <Head>
        {HOTJAR_ID ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:${HOTJAR_ID},hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `,
            }}
          />
        ) : null}
        {GOOGLE_GTM_ID ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GOOGLE_GTM_ID}');`,
            }}
          />
        ) : null}
        {TEALIUM_ENV ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(a,b,c,d){
    a='https://tags.tiqcdn.com/utag/davidsbridal/v7/${TEALIUM_ENV}/utag.js';
    b=document;c='script';d=b.createElement(c);d.src=a;d.type='text/java'+c;d.async=true;
    a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a);
    })();`,
            }}
          />
        ) : null}
        {APPSTORE_ID ? <meta name="apple-itunes-app" content={`app-id=${APPSTORE_ID}`} /> : null}
        {ADOBE_LAUNCH ? <script src={ADOBE_LAUNCH} async /> : null}
        <link rel="manifest" href={`${BASE_PATH}/manifest.json`} />
      </Head>
      {addRecaptcha ? (
        <RecaptchaContext.Provider value={{ ref, executeAsync }}>
          {content}
          <ReCAPTCHA
            ref={ref}
            size="invisible"
            sitekey={RECAPTCHA_SITE_KEY}
            onErrored={reject}
            onChange={(val) => {
              resolve(val);
              ref.current.reset();
            }}
          />
        </RecaptchaContext.Provider>
      ) : (
        content
      )}
    </Fragment>
  );
};

const mapStateToProps = (state: RootState) => ({
  notifications: state.notifications,
  isLoading: state.app.isLoading,
  appName: state.app.appName,
});

export default connect<ReturnType<typeof mapStateToProps>, null, Pick<LayoutProps, 'mainClass'>>(mapStateToProps)(
  Layout,
);
